import React, { useRef, useCallback, useEffect, useState } from 'react';

const ACTION_W = 56;
const THRESHOLD = 48;

/**
 * Tile swipeable (mobile): slide sinistra → play / modifica / elimina.
 * Desktop: solo wrapper (azioni inline gestite dal parent).
 */
export default function SwipeRow({
  rowId,
  openId,
  onOpenChange,
  disabled = false,
  actions = [],
  className = '',
  contentClassName = '',
  shareHandlers = null,
  children,
}) {
  const startX = useRef(0);
  const startY = useRef(0);
  const startOffset = useRef(0);
  const dragging = useRef(false);
  const axis = useRef(null);
  const contentRef = useRef(null);
  const openedBySwipe = useRef(false);
  const [revealing, setRevealing] = useState(false);

  const isOpen = openId === rowId;
  const openWidth = Math.max(ACTION_W, (actions?.length || 0) * ACTION_W);
  const showActions = isOpen || revealing;

  const setOffset = useCallback((x, animate = true) => {
    const el = contentRef.current;
    if (!el) return;
    el.style.transition = animate ? 'transform 0.22s ease' : 'none';
    el.style.transform = `translate3d(${x}px,0,0)`;
  }, []);

  useEffect(() => {
    setOffset(isOpen ? -openWidth : 0, true);
    if (!isOpen) setRevealing(false);
  }, [isOpen, openWidth, setOffset]);

  useEffect(() => {
    if (disabled && isOpen) onOpenChange?.(null);
  }, [disabled, isOpen, onOpenChange]);

  const close = useCallback(() => onOpenChange?.(null), [onOpenChange]);
  const open = useCallback(() => onOpenChange?.(rowId), [onOpenChange, rowId]);

  const onPointerDown = (e) => {
    shareHandlers?.onPointerDown?.(e);
    if (disabled) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target?.closest?.('button, a, input, audio, video, iframe, label')) return;
    dragging.current = true;
    openedBySwipe.current = false;
    axis.current = null;
    startX.current = e.clientX;
    startY.current = e.clientY;
    startOffset.current = isOpen ? -openWidth : 0;
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch (_) {}
  };

  const onPointerMove = (e) => {
    if (!dragging.current || disabled) {
      shareHandlers?.onPointerMove?.(e);
      return;
    }
    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;
    if (!axis.current) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      axis.current = Math.abs(dx) > Math.abs(dy) * 1.15 ? 'x' : 'y';
      if (axis.current === 'y') {
        dragging.current = false;
        shareHandlers?.onPointerMove?.(e);
        return;
      }
      // Scorriamo in orizzontale: mostra azioni e cancella long-press share
      setRevealing(true);
      shareHandlers?.onPointerCancel?.(e);
      shareHandlers?.onPointerUp?.(e);
    }
    if (axis.current !== 'x') return;
    e.preventDefault();
    e.stopPropagation();
    const next = Math.min(0, Math.max(-openWidth, startOffset.current + dx));
    setOffset(next, false);
  };

  const finishDrag = (e) => {
    shareHandlers?.onPointerUp?.(e);
    if (!dragging.current) {
      axis.current = null;
      return;
    }
    dragging.current = false;
    if (axis.current !== 'x') {
      axis.current = null;
      setRevealing(false);
      return;
    }
    axis.current = null;
    const dx = e.clientX - startX.current;
    const projected = startOffset.current + dx;
    const shouldOpen = projected < -THRESHOLD;
    openedBySwipe.current = shouldOpen;
    if (shouldOpen) open();
    else {
      close();
      setRevealing(false);
    }
    setOffset(shouldOpen ? -openWidth : 0, true);
  };

  const onContentClick = (e) => {
    if (isOpen) {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    shareHandlers?.onClick?.(e);
  };

  return (
    <div
      className={`swipe-row ${disabled ? 'swipe-row-disabled' : ''} ${showActions ? 'is-revealing' : ''} ${className}`.trim()}
    >
      {!disabled && actions.length > 0 && (
        <div
          className="swipe-row-actions sm:hidden"
          style={{ width: openWidth }}
          aria-hidden={!showActions}
        >
          {actions.map((action) => (
            <button
              key={action.key}
              type="button"
              className={`swipe-action ${action.tone || ''}`.trim()}
              title={action.label}
              tabIndex={showActions ? 0 : -1}
              onClick={(e) => {
                e.stopPropagation();
                close();
                setRevealing(false);
                action.onClick?.(e);
              }}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}

      <div
        ref={contentRef}
        className={`swipe-row-content ${contentClassName}`.trim()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        onPointerLeave={shareHandlers?.onPointerLeave}
        onContextMenu={shareHandlers?.onContextMenu}
        onClick={onContentClick}
      >
        {children}
      </div>
    </div>
  );
}
